import React from 'react';
import hljs from 'highlight.js/lib/core';
import bash from 'highlight.js/lib/languages/bash';
import go from 'highlight.js/lib/languages/go';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import markdown from 'highlight.js/lib/languages/markdown';
import python from 'highlight.js/lib/languages/python';
import typescript from 'highlight.js/lib/languages/typescript';
import yaml from 'highlight.js/lib/languages/yaml';
import { Check, Copy, Eye, EyeOff, KeyRound, LogIn } from 'lucide-react';
import { getUserData, setStoredApiKey, storedApiKey } from '../lib/userData';

// CodeBlock renders a syntax-highlighted, copy-to-clipboard code sample. Only the
// handful of languages the docs and gateway use are registered so highlight.js
// stays small in the bundle. Highlighting runs at render time (deterministic),
// so the same markup is produced on the server prerender and the first client
// render — hydration-safe. Copy and optional API-key reveal are interactive.

let registered = false;
function ensureLanguages() {
  if (registered) return;
  hljs.registerLanguage('bash', bash);
  hljs.registerLanguage('go', go);
  hljs.registerLanguage('javascript', javascript);
  hljs.registerLanguage('json', json);
  hljs.registerLanguage('markdown', markdown);
  hljs.registerLanguage('python', python);
  hljs.registerLanguage('typescript', typescript);
  hljs.registerLanguage('yaml', yaml);
  registered = true;
}

const LANGUAGE_ALIASES: Record<string, string> = {
  js: 'javascript',
  jsx: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  py: 'python',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  curl: 'bash',
  powershell: 'bash',
  ps1: 'bash',
  yml: 'yaml',
  md: 'markdown',
};

function normalizeLanguage(language?: string): string {
  if (!language) return '';
  const n = language.trim().toLowerCase();
  return LANGUAGE_ALIASES[n] || n;
}

// Placeholders used in copy-paste samples. Copy substitutes a stored key when
// present; Reveal swaps the displayed snippet. $GOBED_* and similar product
// secrets are left alone — they are not the account API key.
export const API_KEY_PLACEHOLDER_RE = /pk_live_(?:\.\.\.|…)|\$APP_API_KEY|\$APP_NZ_KEY|YOUR_API_KEY|YOUR_APP_NZ_API_KEY|\bapp-\.\.\./g;

export function hasApiKeyPlaceholder(code: string): boolean {
  API_KEY_PLACEHOLDER_RE.lastIndex = 0;
  return API_KEY_PLACEHOLDER_RE.test(code);
}

export function substituteApiKey(code: string, key: string): string {
  if (!key) return code;
  API_KEY_PLACEHOLDER_RE.lastIndex = 0;
  return code.replace(API_KEY_PLACEHOLDER_RE, key);
}

export function detectLanguage(code: string, language?: string): string {
  const explicit = normalizeLanguage(language);
  if (explicit) return explicit;
  if (/^\s*curl\b/m.test(code) || /^\s*#!/m.test(code)) return 'bash';
  const t = code.trimStart();
  if (/^(package |func |import \()/.test(t)) return 'go';
  if (/^(from |import \w+|def |async def |client = )/.test(t)) return 'python';
  if (/^(const |let |import |export |function |async function)/.test(t)) return 'javascript';
  if (/^\s*[{[]/.test(t)) return 'json';
  return '';
}

function highlight(code: string, language?: string): string {
  ensureLanguages();
  const lang = detectLanguage(code, language);
  if (lang && hljs.getLanguage(lang)) {
    return hljs.highlight(code, { language: lang, ignoreIllegals: true }).value;
  }
  return escapeHtml(code);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const btnClass =
  'inline-flex items-center gap-1 rounded-md border border-white/10 bg-black/80 px-2 py-1 text-[11px] font-mono text-white/60 transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-cyan-300/60';

export interface CodeBlockProps {
  code: string;
  language?: string;
  className?: string;
  preClassName?: string;
  showCopy?: boolean;
  testId?: string;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({
  code,
  language,
  className,
  preClassName,
  showCopy = true,
  testId,
}) => {
  const [copied, setCopied] = React.useState(false);
  const [apiKey, setApiKey] = React.useState('');
  const [revealed, setRevealed] = React.useState(false);
  const [picker, setPicker] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [signedIn, setSignedIn] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const canInject = hasApiKeyPlaceholder(code);

  React.useEffect(() => {
    if (!canInject) return;
    setApiKey(storedApiKey());
    getUserData()
      .then((d) => setSignedIn(d.authenticated))
      .catch(() => setSignedIn(false));
  }, [canInject]);

  const displayed = revealed && apiKey ? substituteApiKey(code, apiKey) : code;
  const html = highlight(displayed, language);

  const persist = (key: string) => {
    const next = key.trim();
    setStoredApiKey(next);
    setApiKey(next);
    if (next) setRevealed(true);
    setPicker(false);
    setError('');
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey ? substituteApiKey(code, apiKey) : code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — non-fatal */
    }
  };

  const toggleReveal = () => {
    if (apiKey) {
      setRevealed((v) => !v);
      setPicker(false);
      return;
    }
    setPicker((v) => !v);
  };

  const createKey = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/keys', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'docs' }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 401) {
        setError('Sign in to create a key.');
        return;
      }
      if (!res.ok || !body.key) {
        setError(typeof body.error === 'string' ? body.error : 'Could not create a key.');
        return;
      }
      persist(body.key as string);
    } catch {
      setError('Could not create a key.');
    } finally {
      setBusy(false);
    }
  };

  const loginHref =
    typeof window === 'undefined' ? '/login?next=/docs' : `/login?next=${encodeURIComponent(window.location.pathname)}`;

  return (
    <div className={`relative min-w-0 ${className || ''}`}>
      {showCopy && (
        <div className="absolute right-2 top-2 z-10 flex flex-wrap items-center justify-end gap-1">
          {canInject && (
            <button type="button" onClick={toggleReveal} aria-label={revealed ? 'Hide API key' : 'Reveal API key'} className={btnClass} data-testid="code-reveal-key">
              {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
              {revealed ? 'Hide key' : 'Reveal API key'}
            </button>
          )}
          <button type="button" onClick={copy} aria-label="Copy code" className={btnClass} data-testid="code-copy">
            {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}
      {picker && (
        <div className="absolute right-2 top-10 z-20 w-[min(100%-1rem,20rem)] rounded-lg border border-white/15 bg-[#0d0d0f] p-3 shadow-[0_18px_60px_rgba(0,0,0,0.6)]" data-testid="code-key-picker">
          <label className="block">
            <span className="mb-1 flex items-center gap-1 text-[11px] font-mono text-white/45">
              <KeyRound size={12} /> Your API key
            </span>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              type="password"
              placeholder="pk_live_…"
              autoComplete="off"
              className="w-full rounded-md border border-white/12 bg-black px-2.5 py-1.5 font-mono text-xs text-white outline-none focus:border-cyan-300/50"
            />
          </label>
          {error && <p className="mt-1.5 text-[11px] text-rose-300/80">{error}</p>}
          <div className="mt-2 flex flex-wrap gap-1.5">
            <button type="button" onClick={() => persist(draft)} disabled={!draft.trim()} className={`${btnClass} disabled:opacity-40`}>
              Use key
            </button>
            {signedIn ? (
              <button type="button" onClick={createKey} disabled={busy} className={btnClass}>
                {busy ? 'Creating…' : 'Create key'}
              </button>
            ) : (
              <a href={loginHref} className={btnClass}>
                <LogIn size={12} /> Sign in
              </a>
            )}
          </div>
        </div>
      )}
      <pre
        data-testid={testId}
        className={`max-w-full overflow-x-auto rounded-xl border border-white/10 bg-[#050505] p-4 text-xs leading-6 ${showCopy ? 'pt-11' : ''} ${preClassName || ''}`}
      >
        <code
          className="hljs block w-max min-w-full font-mono"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </pre>
    </div>
  );
};
